import PSound from 'pixi-sound';
import { Application, TextRenderer, CaptionPlayer, CaptionData } from 'springroll';
import Sound = PSound.Sound;
import { GameTime } from './GameTime';

interface ConfigItem
{
    path: string;
    minVolume?: number;
    video?: string;
    caption: {content:string, start:number, end:number}[];
}

interface Config
{
    [id: string]: ConfigItem;
}

class VO
{
    private sound: Sound;
    private path: string;
    public videoPath: string|null;
    public loaded: Promise<void>;
    private playing: boolean;
    public _volume: number;
    public minVolume: number;

    constructor(path:string)
    {
        this.playing = false;
        this.path = path;
        this.sound = null;
        this.loaded = null;
        this.minVolume = 0;
    }

    public get duration() { return this.sound?.duration }

    public set volume(v:number)
    {
        if (this.minVolume)
        {
            v = Math.max(v, this.minVolume);
        }
        this._volume = v;
        if (this.sound)
        {
            this.sound.volume = v;
        }
    }

    public async play(): Promise<[PSound.IMediaInstance, Promise<void>]>
    {
        this.playing = true;
        if (!this.sound)
        {
            this.load();
        }
        await this.loaded;
        if (!this.playing)
        {
            return Promise.reject('cancelled while loading');
        }
        if (this.sound)
        {
            let instance: PSound.IMediaInstance|Promise<PSound.IMediaInstance>;
            let complete = new Promise<void>(resolve => {
                instance = this.sound.play(() => resolve());
            });
            return [await instance, complete];
        }
    }

    public stop()
    {
        this.playing = false;
        if (this.sound)
        {
            this.sound.stop();
        }
    }

    public load(): Promise<void>
    {
        if (!this.loaded)
        {
            this.loaded = new Promise<void>((resolve, reject) => {
                Sound.from({
                    url: this.path,
                    preload: true,
                    loaded: (err, sound) => {
                        if (err)
                        {
                            reject(err);
                        }
                        else
                        {
                            this.sound = sound;
                            sound.volume = this._volume;
                            resolve();
                        }
                    }
                });
            });
        }
        return this.loaded;
    }

    public unload(): void
    {
        this.playing = false;
        if (this.sound)
        {
            this.sound.destroy();
        }
        else if (this.loaded)
        {
            this.loaded.then(() => this.sound.destroy());
        }
        this.sound = null;
        this.loaded = null;
    }
}

class VOSequence
{
    /**
     * List of files to play in order.
     */
    private sequence: string[];
    /**
     * Currently playing instance (used for timing)
     */
    public instance: PSound.IMediaInstance;
    /**
     * Index of current file
     */
    private index: number;
    /**
     * If current list of VO is a sequence (list of separate VO lines)
     * vs a dynamic line (list of VO files combined into one "line")
     */
    private isSequence: boolean;
    /**
     * If locked, then VO can not be interrupted by new VO (I.E. block rollover descriptions
     * during unskippable dialogue lines)
     */
    public priority: VOPriority;

    /**
     * Unique ID of this sequence of VO - just the individual ids joined together.
     */
    public uid: string;

    protected pauseLocked: boolean;

    protected manager: VOManager;

    protected onInterrupt: () => void;

    constructor(manager: VOManager, sequence: string[], isSequence: boolean, priority: VOPriority, onInterrupt?: () => void)
    {
        this.playNext = this.playNext.bind(this);
        this.manager = manager;
        this.index = 0;
        this.sequence = sequence;
        this.isSequence = isSequence;
        this.priority = priority;
        this.instance = null;
        this.onInterrupt = onInterrupt;
        this.uid = sequence.join('');
        this.pauseLocked = false;
    }

    public async play()
    {
        const ids = this.sequence;
        this.index = -1;
        // start VO
        let chain = this.playNext();
        for (let i = 0; i < ids.length; ++i)
        {
            chain = chain.then(this.playNext);
        }
        try
        {
            await chain;
        }
        catch(err)
        {
            console.warn('Audio playback failed: ', err);
        };
    }

    private async playNext()
    {
        if (!this.sequence || ++this.index >= this.sequence.length)
        {
            this.sequence = null;
            return;
        }
        const id = this.sequence[this.index];
        const result = await this.manager.vo[id].play();
        // start captions, now that the audio is playing
        if (this.isSequence)
        {
            this.manager.playCaption(id, this);
        }
        else if (this.index === 0)
        {
            this.manager.playCaption(this.priority === VOPriority.Highlight ? 'DYNAMIC2' : 'DYNAMIC', this);
        }
        this.instance = result[0];
        return result[1];
    }

    public pause(lock = false)
    {
        if (this.instance)
        {
            this.instance.paused = true;
            this.pauseLocked = lock || this.pauseLocked;
        }
    }

    public unpause(unlock = false)
    {
        if (this.pauseLocked)
        {
            if (unlock)
            {
                this.pauseLocked = false;
            }
            else
            {
                return;
            }
        }
        this.instance.paused = false;
        this.restoreCaption();
    }

    public get paused() { return this.instance?.paused; }

    public restoreCaption()
    {
        const id = this.sequence[this.index];
        if (this.isSequence)
        {
            this.manager.playCaption(id, this, (this.instance?.progress || 0) * this.manager.vo[id].duration * 1000);
        }
        else
        {
            this.manager.playCaption(this.priority === VOPriority.Highlight ? 'DYNAMIC2' : 'DYNAMIC', this);
        }
    }

    public stop()
    {
        if (this.instance)
        {
            this.onInterrupt?.();
        }
        this.manager.vo[this.sequence[this.index]].stop();
        this.instance = null;
    }
}

class VideoSequence extends VOSequence
{
    private video: HTMLVideoElement;
    private voSeq: VOSequence;

    constructor(manager: VOManager, video: HTMLVideoElement, voId: string, priority: VOPriority, onInterrupt?: () => void)
    {
        super(manager, [], true, priority, onInterrupt);

        this.video = video;
        this.uid = voId;
        this.voSeq = new VOSequence(manager, [voId], true, priority, onInterrupt);

        // this.update = this.update.bind(this);
    }

    public async play()
    {
        // try to brute force inline playback, per https://stackoverflow.com/a/39566346
        // this may be a problem in the cordova-based PBS kids app
        try {
            (this.video as any).webkitExitFullScreen();
            this.video.controls = false;
        }
        catch (e)
        {
            // eat the error
        }
        this.video.play();
        await this.voSeq.play();
        this.video.pause();
    }

    public restoreCaption()
    {
        this.voSeq.restoreCaption();
    }

    public pause(lock = false)
    {
        this.pauseLocked = lock || this.pauseLocked;
        this.voSeq.pause();
        this.video.pause();
    }

    public unpause(unlock = false)
    {
        if (this.pauseLocked)
        {
            if (unlock)
            {
                this.pauseLocked = false;
            }
            else
            {
                return;
            }
        }
        // seek video to audio pos just to ensure they are aligned
        this.video.onseeked = () => {
            this.video.onseeked = null;
            this.voSeq.unpause();
            this.video.play();
        };
        this.video.currentTime = ((this.voSeq.instance?.progress || 0) * this.manager.vo[this.uid].duration);
    }

    public get paused() {
        return this.video.paused;
    }

    public stop()
    {
        this.onInterrupt?.();
        this.voSeq.stop();
        this.video.pause();
    }
}

/**
 * VO Priority - higher priority can interrupt equal or lower priority VO,
 * lower priority VO can't interrupt higher priority VO.
 */
enum VOPriority
{
    Highlight = 0,
    Standard = 1,
    // AudioDescription = 2,
}

export interface DynamicCaptionInsert
{
    insert: string;
    respectSpace?: boolean;
}

export class VOManager
{
    private captions: CaptionPlayer;
    /** Internal use only! */
    vo: {[id:string]: VO};
    private captionConfig: CaptionData;
    private captionsMuted: boolean;

    private currentHighlight: VOSequence;
    private currentNormal: VOSequence;
    private captionSource: VOSequence;

    private volumeMultiplier: number;

    public overrideInterrupt: boolean;
    public overrideUnpause: boolean;
    public tutorial: boolean;


    constructor(app: Application, config: Config, volumeMultiplier: number)
    {
        this.overrideInterrupt = false;
        this.overrideUnpause = false;
        this.tutorial = false;

        if (!config)
        {
            throw new Error('VO JSON failed to parse and no VO will function! Please correct any syntax errors in the file.');
        }
        this.vo = {};

        this.volumeMultiplier = volumeMultiplier;
        const voVolume = app.state.voVolume;

        const captionConfig = this.captionConfig = {} as CaptionData;
        for (const id in config)
        {
            if (config[id].caption)
            {
                captionConfig[id] = config[id].caption;
            }
            if (config[id].path)
            {
                const vo = this.vo[id] = new VO(config[id].path);
                if (config[id].minVolume)
                {
                    vo.minVolume = config[id].minVolume;
                }
                vo.videoPath = config[id].video || null;
                vo.volume = voVolume.value * this.volumeMultiplier;
            }
        }
        // add caption to be overwritten each time we want to play a dynamically assembled string
        // of files
        captionConfig.DYNAMIC = [{
            content: 'TBD',
            start: 0,
            end: 0
        }];

        captionConfig.DYNAMIC2 = [{
            content: 'TBD',
            start: 0,
            end: 0
        }];

        this.captions = new CaptionPlayer(captionConfig, new TextRenderer(document.getElementById('captions')));

        this.captionsMuted = app.state.captionsMuted.value;
        // Captions toggle from container.
        app.state.captionsMuted.subscribe((value) =>
        {
            this.captionsMuted = value;
            if (value)
            {
                this.captions.stop();
            }
            else if(this.captionSource && !this.captionSource.paused)
            {
                this.captionSource.restoreCaption();
            }
        });

        voVolume.subscribe((value) => {
            for (const id in this.vo)
            {
                this.vo[id].volume = value * this.volumeMultiplier;
            }
        });

        // subscribe to game ticks to update captions regularly - convert ms to sec
        GameTime.gameTick.subscribe(value => this.captions.update(value / 1000));
    }

    public isPlaying(id:string)
    {
        return this.currentNormal?.uid.includes(id);
    }

    public get playing() { return !!this.currentNormal || !!this.currentHighlight; }

    public async playVO(ids:string|string[], isSequence?: true, onInterrupt?: () => void): Promise<void>;
    public async playVO(ids:string|(string|DynamicCaptionInsert)[], isSequence: false, onInterrupt?: () => void): Promise<void>;
    public async playVO(ids:string|(string|DynamicCaptionInsert)[], isSequence = true, onInterrupt?: () => void): Promise<void>
    {
        return this.play(ids, isSequence, VOPriority.Standard, onInterrupt);
    }

    public async playHighlight(ids:string|string[], isSequence?: true, onInterrupt?: () => void): Promise<void>;
    public async playHighlight(ids:string|(string|DynamicCaptionInsert)[], isSequence: false, onInterrupt?: () => void): Promise<void>;
    public async playHighlight(ids:string|(string|DynamicCaptionInsert)[], isSequence = true, onInterrupt?: () => void): Promise<void>
    {
        // interrupt instruction VO to play hover VO
        if(!this.overrideInterrupt)
        {
            this.pause(true);
            await this.play(ids, isSequence, VOPriority.Highlight, onInterrupt);
            this.unpause(true);
        }
        else {
            await this.play(ids, isSequence, VOPriority.Highlight, onInterrupt);
        }
        
        return;
    }

    private async play(ids:string|(string|DynamicCaptionInsert)[], isSequence = true, priority = VOPriority.Highlight, onInterrupt?: () => void): Promise<void>
    {
        // bail if new VO is lower priority than existing VO
        if (priority > VOPriority.Highlight && this.currentNormal?.priority > priority)
        {
            return;
        }
        if (typeof ids === 'string')
        {
            ids = [ids];
            isSequence = true;
        }
        // if playing a highlight VO that is already playing, don't play it
        if (priority === VOPriority.Highlight)
        {
            if (this.currentHighlight?.uid === ids.join(''))
            {
                return;
            }
        }
        else if (this.currentNormal?.paused && this.currentNormal.uid === ids.join(''))
        {
            this.currentNormal.unpause();
            return;
        }
        this.stopChannel(priority);

        if (!isSequence)
        {
            const dynamic = priority === VOPriority.Highlight ?
                (this.captions.captions as any).DYNAMIC2.lines[0] :
                (this.captions.captions as any).DYNAMIC.lines[0];
            dynamic.content = '';
            dynamic.endTime = 0;
            let needsSpacer = false;
            for (const id of ids)
            {
                if (typeof id === 'string')
                {
                    if (!this.captionConfig[id])
                    {
                        console.error(`No caption found for id ${id}`);
                        continue;
                    }
                    const caption = this.captionConfig[id][0];
                    if (needsSpacer)
                    {
                        dynamic.content += ' ';
                    }
                    dynamic.content += caption.content;
                    dynamic.endTime += caption.end;
                    needsSpacer = true;
                }
                else
                {
                    if (needsSpacer && id.respectSpace)
                    {
                        dynamic.content += ' ';
                    }
                    dynamic.content += id.insert;
                    needsSpacer = false;
                }
            }
        }
        for (const id of ids)
        {
            if (typeof id !== 'string') continue;
            if (!this.vo[id])
            {
                return Promise.reject(new Error(`Unknown VO id: ${id}`));
            }
            this.vo[id].load();
        }
        const sequence = new VOSequence(this, ids.filter(id => typeof id === 'string') as string[], isSequence, priority, onInterrupt);
        if (priority === VOPriority.Highlight)
        {
            this.currentHighlight = sequence;
        }
        else
        {
            this.currentNormal = sequence;
        }
        // play sequence and add a hook for completion
        await sequence.play();
        this.completed(sequence);
    }

    public async preloadVideo(video: HTMLVideoElement, videoName:string)
    {
        const vo = this.vo[videoName];
        vo._volume = 0.08;

        const videoLoad = new Promise((resolve, reject) => {
            video.src = vo.videoPath;
            video.oncanplaythrough = resolve;
            video.onerror = reject;
            video.load();
        });
        await Promise.all([videoLoad, vo.load()]);
    }

    public async playVideo(video: HTMLVideoElement, voId: string)
    {
        if (this.currentNormal instanceof VideoSequence && voId === this.currentNormal.uid)
        {
            await this.currentNormal.play();
            this.completed(this.currentNormal);
            return;
        }
        const priority = VOPriority.Standard;
        this.stopChannel(priority);
        const sequence = new VideoSequence(this, video, voId, priority);
        this.currentNormal = sequence;
        await sequence.play();
        this.completed(sequence);
    }

    /**
     * @param lock If true, "locks" this pause so that unpause must also be passed "true" to unpause. This allows manually paused media to not be unpaused by regaining window focus.
     */
    public pause(lock = false)
    {
        if (this.currentNormal)
        {
            this.currentNormal.pause(lock);
            this.captions.stop();
        }
    }

    public unpause(unlock = false)
    {
        this.currentNormal?.unpause(unlock);
    }

    public pauseAll()
    {
        this.currentNormal?.pause();
        this.currentHighlight?.pause();
        this.captions.stop();
    }

    public unpauseAll()
    {
        this.currentNormal?.unpause();
        this.currentHighlight?.unpause();
    }

    /**
     * For internal use. Plays a caption.
     */
    playCaption(id:string, source: VOSequence, time = 0)
    {
        if (!this.captionsMuted && this.captionConfig[id])
        {
            this.captionSource = source;
            this.captions.start(id, time);
        }
    }

    private stopChannel(priority: VOPriority)
    {
        if (priority === VOPriority.Highlight)
        {
            if (this.captionSource === this.currentHighlight)
            {
                this.captions.stop();
                this.captionSource = null;
            }
            this.currentHighlight?.stop();
            this.currentHighlight = null;
        }
        else
        {
            if (this.captionSource === this.currentNormal)
            {
                this.captions.stop();
                this.captionSource = null;
            }
            this.currentNormal?.stop();
            this.currentNormal = null;
        }
    }

    /**
     * Clear references to a completed VO Sequence.
     */
    private completed(source: VOSequence)
    {
        if (source === this.captionSource)
        {
            this.captionSource = null;
        }
        if (source === this.currentHighlight)
        {
            this.currentHighlight = null;
            // restore previously playing line
            if (this.currentNormal?.paused === false)
            {
                this.currentNormal.restoreCaption();
            }
        }
        if (source === this.currentNormal)
        {
            this.currentNormal = null;
        }
    }

    public stop()
    {
        this.currentHighlight?.stop();
        this.currentHighlight = null;
        this.currentNormal?.stop();
        this.currentNormal = null;
        this.captionSource = null;
        this.captions.stop();
    }

    /**
    * Stops just the standard VO channel, in the off chance you want to allow highlight VO to
    * continue.
    */
    public stopVO()
    {
        if (this.captionSource === this.currentNormal)
        {
            this.captionSource = null;
            this.captions.stop();
        }
        this.currentNormal?.stop();
        this.currentNormal = null;
    }

    public unloadAll()
    {
        this.stop();
        for (const id in this.vo)
        {
            this.vo[id].unload();
        }
    }
}